import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';
import { cn } from '@/lib/utils';
import { DrawerShell } from './item-drawers';
import { useCopyCatalog } from './business-item.api';

/**
 * Copy one store's whole catalogue onto others.
 *
 * A blunt instrument, so it says what it will do before it does it: this is
 * every item the source stocks, landing on stores that may already stock their
 * own. The original offered one destination at a time even though the endpoint
 * takes a list; this offers the list.
 */
export function CopyCatalogSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data: businesses, isLoading: storesLoading } = useBusinesses();
  const copy = useCopyCatalog();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);

  const stores = businesses ?? [];
  const targets = stores.filter((store) => store.id !== from);
  const chosen = to.filter((id) => id !== from);
  const source = stores.find((store) => store.id === from) ?? null;

  return (
    <DrawerShell
      title={t('itemMaster.copyCatalog')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <p className="text-muted-foreground text-sm">
        {t('itemMaster.copyCatalogHint')}
      </p>

      <div className="space-y-1.5">
        <Label>{t('itemMaster.sourceStore')}</Label>
        {storesLoading ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <Select
            value={from}
            onValueChange={(value) => {
              setFrom(value);
              setTo([]);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('itemMaster.pickSourceStore')} />
            </SelectTrigger>
            <SelectContent>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {source && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label>{t('itemMaster.destinationStores')}</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() =>
                setTo(
                  chosen.length === targets.length
                    ? []
                    : targets.map((store) => store.id)
                )
              }
            >
              {chosen.length === targets.length
                ? t('itemMaster.clearAll')
                : t('itemMaster.selectAll')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {targets.map((store) => {
              const picked = chosen.includes(store.id);
              return (
                <button
                  key={store.id}
                  type="button"
                  onClick={() =>
                    setTo((current) =>
                      picked
                        ? current.filter((id) => id !== store.id)
                        : [...current, store.id]
                    )
                  }
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors',
                    picked
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent'
                  )}
                >
                  {store.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            className="w-full"
            disabled={!source || chosen.length === 0 || copy.isPending}
          >
            {copy.isPending && <Loader2 className="animate-spin" />}
            {t('itemMaster.copyCatalogNow')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('itemMaster.copyCatalog')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('itemMaster.copyCatalogPrompt', {
                store: source?.name ?? '',
                count: chosen.length,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                copy.mutate(
                  { fromId: from, toIds: chosen },
                  {
                    onSuccess: () => {
                      toast.success(t('itemMaster.catalogCopied'));
                      onOpenChange(false);
                    },
                    onError: () => toast.error(t('itemMaster.actionFailed')),
                  }
                )
              }
            >
              {t('itemMaster.copyCatalogNow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DrawerShell>
  );
}
